import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useKbSearchState } from "@/lib/kb-search-state";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  BookOpen, Search, X, ArrowLeft, ThumbsUp, ThumbsDown,
  Loader2, Library, FileText, ChevronRight, FolderOpen, GripHorizontal,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

type KnowledgeBase = {
  id: string;
  name: string;
  description: string | null;
  articleCount: number;
};

type KbCategory = {
  id: string;
  name: string;
  knowledgeBaseId: string;
  sortOrder: number;
};

type KbArticle = {
  id: string;
  title: string;
  content: string;
  status: string;
  sourceType: string;
  categoryId: string | null;
  searchKeywords: string | null;
  knowledgeBaseId: string;
  createdAt: string;
  updatedAt: string;
};

type ArticleDetail = KbArticle & {
  feedback: { helpful: number; notHelpful: number };
  userFeedback: { isHelpful: boolean } | null;
};

export function KbSearchHeaderButton() {
  const { toggle, isOpen } = useKbSearchState();

  const { data: kbEnabled } = useQuery<{ enabled: boolean; aiSearchEnabled: boolean }>({
    queryKey: ["/api/kb/enabled"],
  });

  if (!kbEnabled?.enabled) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      className={`relative h-8 w-8 ${isOpen ? "text-primary" : ""}`}
      title="Knowledge Base"
      data-testid="button-kb-search-toggle"
    >
      <BookOpen className="h-4 w-4" />
    </Button>
  );
}

export function FloatingKbSearch() {
  const { isOpen, close } = useKbSearchState();
  const dragConstraintsRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedKbId, setSelectedKbId] = useState<string | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 2000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { toast } = useToast();

  const { data: kbEnabled } = useQuery<{ enabled: boolean; aiSearchEnabled: boolean }>({
    queryKey: ["/api/kb/enabled"],
    staleTime: 30000,
    refetchInterval: 60000,
  });

  const { data: kbsData, isLoading: kbsLoading } = useQuery<KnowledgeBase[]>({
    queryKey: ["/api/knowledge-bases"],
    enabled: isOpen && !!kbEnabled?.enabled,
  });
  const kbs = kbsData ?? [];

  const { data: categoriesData } = useQuery<KbCategory[]>({
    queryKey: ["/api/knowledge-bases", selectedKbId, "categories"],
    enabled: isOpen && !!selectedKbId,
    queryFn: async () => {
      const res = await fetch(`/api/knowledge-bases/${selectedKbId}/categories`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  const categories = categoriesData ?? [];

  const { data: articlesData, isLoading: articlesLoading } = useQuery<KbArticle[]>({
    queryKey: ["/api/knowledge-bases", selectedKbId, "articles", selectedCategoryId, "floating"],
    enabled: isOpen && !!selectedKbId && !selectedArticleId,
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("status", "published");
      if (selectedCategoryId) params.set("categoryId", selectedCategoryId);
      const res = await fetch(`/api/knowledge-bases/${selectedKbId}/articles?${params}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  const articles = articlesData ?? [];

  const { data: searchResultsData, isLoading: searching } = useQuery<KbArticle[]>({
    queryKey: ["/api/kb/agent/search", debouncedQuery, "floating"],
    enabled: isOpen && debouncedQuery.length >= 2 && !selectedKbId && !selectedArticleId,
    queryFn: async () => {
      const res = await fetch(`/api/kb/agent/search?q=${encodeURIComponent(debouncedQuery)}`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });
  const searchResults = searchResultsData ?? [];

  const { data: aiAnswer, isLoading: aiAnswerLoading } = useQuery<{ answer: string | null; sourceArticleId: string | null; sourceArticleTitle: string | null }>({
    queryKey: ["/api/kb/agent/ai-answer", debouncedQuery, "floating"],
    enabled: isOpen && debouncedQuery.length >= 5 && !selectedKbId && !selectedArticleId && searchResults.length > 0 && !!kbEnabled?.aiSearchEnabled,
    queryFn: async () => {
      const res = await fetch(`/api/kb/agent/ai-answer?q=${encodeURIComponent(debouncedQuery)}`, { credentials: "include" });
      if (!res.ok) return { answer: null, sourceArticleId: null, sourceArticleTitle: null };
      return res.json();
    },
    staleTime: 60000,
  });

  const { data: articleDetail, isLoading: loadingDetail } = useQuery<ArticleDetail>({
    queryKey: ["/api/kb-articles", selectedArticleId],
    enabled: isOpen && !!selectedArticleId,
  });

  const feedbackMutation = useMutation({
    mutationFn: async (isHelpful: boolean) => {
      return apiRequest("POST", `/api/kb-articles/${selectedArticleId}/feedback`, { isHelpful });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kb-articles", selectedArticleId] });
      toast({ title: "Thanks for your feedback!" });
    },
  });

  function handleClose() {
    close();
    setSearchQuery("");
    setSelectedKbId(null);
    setSelectedArticleId(null);
    setSelectedCategoryId(null);
  }

  function goBackFromArticle() {
    setSelectedArticleId(null);
  }

  function goBackFromKb() {
    setSelectedKbId(null);
    setSelectedCategoryId(null);
  }

  if (!kbEnabled?.enabled) return null;

  const isSearching = searchQuery.length >= 2 && !selectedKbId && !selectedArticleId;
  const isDebouncing = isSearching && searchQuery !== debouncedQuery;
  const selectedKb = kbs.find(kb => kb.id === selectedKbId);

  return (
    <>
      <div ref={dragConstraintsRef} className="fixed inset-0 pointer-events-none z-40" />
      <AnimatePresence>
        {isOpen && (
          <motion.div
            drag
            dragConstraints={dragConstraintsRef}
            dragMomentum={false}
            dragElastic={0}
            onDragStart={() => setIsDragging(true)}
            onDragEnd={() => setTimeout(() => setIsDragging(false), 50)}
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-4 left-4 z-50"
            style={{ touchAction: "none" }}
          >
            <div
              className="w-96 rounded-lg shadow-2xl border bg-card overflow-hidden flex flex-col"
              style={{ maxHeight: "520px" }}
              data-testid="card-floating-kb-search"
            >
              <div
                className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 cursor-grab active:cursor-grabbing select-none"
                data-testid="kb-search-header"
              >
                <div className="flex items-center gap-2">
                  <GripHorizontal className="w-4 h-4 text-muted-foreground" />
                  <BookOpen className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold">Knowledge Base</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  onClick={handleClose}
                  data-testid="button-kb-search-close"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>

              {selectedArticleId ? (
                <ArticleView
                  articleDetail={articleDetail}
                  loading={loadingDetail}
                  onBack={goBackFromArticle}
                  onFeedback={(isHelpful) => feedbackMutation.mutate(isHelpful)}
                  feedbackPending={feedbackMutation.isPending}
                />
              ) : selectedKbId && selectedKb ? (
                <KbDetailView
                  kb={selectedKb}
                  categories={categories}
                  articles={articles}
                  loading={articlesLoading}
                  selectedCategoryId={selectedCategoryId}
                  onSelectCategory={setSelectedCategoryId}
                  onSelectArticle={(id) => setSelectedArticleId(id)}
                  onBack={goBackFromKb}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
              ) : (
                <KbListView
                  kbs={kbs}
                  loading={kbsLoading}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  isSearching={isSearching}
                  isDebouncing={isDebouncing}
                  searching={searching}
                  searchResults={searchResults}
                  onSelectKb={(id) => { setSelectedKbId(id); setSearchQuery(""); }}
                  onSelectArticle={(id) => setSelectedArticleId(id)}
                  kbNameLookup={kbs}
                  aiAnswer={aiAnswer}
                  aiAnswerLoading={aiAnswerLoading}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function KbListView({
  kbs, loading, searchQuery, onSearchChange,
  isSearching, isDebouncing, searching, searchResults,
  onSelectKb, onSelectArticle, kbNameLookup,
  aiAnswer, aiAnswerLoading,
}: {
  kbs: KnowledgeBase[];
  loading: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  isSearching: boolean;
  isDebouncing: boolean;
  searching: boolean;
  searchResults: KbArticle[];
  onSelectKb: (id: string) => void;
  onSelectArticle: (id: string) => void;
  kbNameLookup: KnowledgeBase[];
  aiAnswer?: { answer: string | null; sourceArticleId: string | null; sourceArticleTitle: string | null };
  aiAnswerLoading?: boolean;
}) {
  return (
    <div className="flex flex-col" style={{ maxHeight: "470px" }}>
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search articles..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="input-floating-kb-search"
            autoFocus
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto kb-scrollbar" style={{ maxHeight: "420px" }}>
        <div className="p-2 pr-3">
          {isSearching && (searching || isDebouncing) && (
            <div className="flex justify-center p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}

          {isSearching && !searching && !isDebouncing && searchResults.length === 0 && (
            <div className="flex flex-col items-center p-6 text-center">
              <Search className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No results found</p>
            </div>
          )}

          {isSearching && searchResults.length > 0 && (
            <div className="space-y-1">
              {(aiAnswerLoading || aiAnswer?.answer) && (
                <div className="mx-1 mb-2 p-2.5 rounded-lg bg-primary/5 border border-primary/20" data-testid="floating-ai-answer">
                  {aiAnswerLoading ? (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Generating answer...</span>
                    </div>
                  ) : aiAnswer?.answer ? (
                    <div>
                      <div className="flex items-center gap-1.5 mb-1">
                        <div className="w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
                          <Search className="w-2.5 h-2.5 text-primary" />
                        </div>
                        <span className="text-[10px] font-semibold text-primary uppercase tracking-wider">AI Answer</span>
                      </div>
                      <p className="text-sm leading-relaxed" data-testid="text-floating-ai-answer">{aiAnswer.answer}</p>
                      {aiAnswer.sourceArticleTitle && (
                        <button
                          className="text-[10px] text-primary/70 hover:text-primary mt-1.5 hover:underline"
                          onClick={() => aiAnswer.sourceArticleId && onSelectArticle(aiAnswer.sourceArticleId)}
                        >
                          Source: {aiAnswer.sourceArticleTitle}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
              <p className="text-xs text-muted-foreground px-2 mb-1">{searchResults.length} results</p>
              {searchResults.map(article => (
                <CompactArticleItem
                  key={article.id}
                  article={article}
                  onClick={() => onSelectArticle(article.id)}
                  kbName={kbNameLookup.find(kb => kb.id === article.knowledgeBaseId)?.name}
                  searchQuery={searchQuery}
                />
              ))}
            </div>
          )}

          {!isSearching && loading && (
            <div className="flex justify-center p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          )}

          {!isSearching && !loading && (kbs.length === 0 || kbs.every(kb => kb.articleCount === 0)) && (
            <div className="flex flex-col items-center p-6 text-center">
              <BookOpen className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No knowledge bases available</p>
            </div>
          )}

          {!isSearching && !loading && kbs.filter(kb => kb.articleCount > 0).map(kb => (
            <div
              key={kb.id}
              className="flex items-center gap-3 p-2.5 rounded-md cursor-pointer hover:bg-accent/50 transition-colors group"
              onClick={() => onSelectKb(kb.id)}
              data-testid={`floating-kb-${kb.id}`}
            >
              <div className="p-1.5 rounded-md bg-primary/10 text-primary shrink-0">
                <Library className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{kb.name}</p>
                {kb.description && <p className="text-xs text-muted-foreground truncate">{kb.description}</p>}
              </div>
              <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0">
                <FileText className="w-3 h-3" />
                <span>{kb.articleCount}</span>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function KbDetailView({
  kb, categories, articles, loading, selectedCategoryId,
  onSelectCategory, onSelectArticle, onBack, searchQuery, onSearchChange,
}: {
  kb: KnowledgeBase;
  categories: KbCategory[];
  articles: KbArticle[];
  loading: boolean;
  selectedCategoryId: string | null;
  onSelectCategory: (id: string | null) => void;
  onSelectArticle: (id: string) => void;
  onBack: () => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
}) {
  const q = searchQuery.toLowerCase().trim();
  const filteredArticles = q.length >= 2
    ? articles.filter(a => a.title.toLowerCase().includes(q) || a.content.toLowerCase().includes(q) || (a.searchKeywords && a.searchKeywords.toLowerCase().includes(q)))
    : articles;
  const uncategorized = filteredArticles.filter(a => !a.categoryId);
  const categorized = categories.map(cat => ({
    ...cat,
    articles: filteredArticles.filter(a => a.categoryId === cat.id),
  })).filter(cat => cat.articles.length > 0);
  const isKbSearching = q.length >= 2;

  return (
    <div className="flex flex-col" style={{ maxHeight: "470px" }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onBack} data-testid="button-floating-kb-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate">{kb.name}</p>
        </div>
      </div>

      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search in this KB..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="pl-8 h-9 text-sm"
            data-testid="input-floating-kb-detail-search"
          />
        </div>
      </div>

      {categories.length > 1 && (
        <div className="flex gap-1.5 px-3 py-1.5 flex-wrap">
          <Badge
            variant={!selectedCategoryId ? "default" : "outline"}
            className="cursor-pointer text-xs px-2 py-0.5"
            onClick={() => onSelectCategory(null)}
          >
            All
          </Badge>
          {categories.map(cat => (
            <Badge
              key={cat.id}
              variant={selectedCategoryId === cat.id ? "default" : "outline"}
              className="cursor-pointer text-xs px-2 py-0.5"
              onClick={() => onSelectCategory(cat.id)}
            >
              {cat.name}
            </Badge>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto kb-scrollbar" style={{ maxHeight: "360px" }}>
        <div className="p-2 pr-3">
          {loading ? (
            <div className="flex justify-center p-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : filteredArticles.length === 0 ? (
            <div className="flex flex-col items-center p-6 text-center">
              <FileText className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">{isKbSearching ? "No matching articles" : "No articles available"}</p>
            </div>
          ) : (isKbSearching || selectedCategoryId) ? (
            <div className="space-y-1">
              {filteredArticles.map(article => (
                <CompactArticleItem key={article.id} article={article} onClick={() => onSelectArticle(article.id)} searchQuery={isKbSearching ? q : undefined} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {categorized.map(cat => (
                <div key={cat.id}>
                  <div className="flex items-center gap-1.5 px-2 mb-1">
                    <FolderOpen className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{cat.name}</span>
                    <span className="text-xs text-muted-foreground">({cat.articles.length})</span>
                  </div>
                  <div className="space-y-1">
                    {cat.articles.map(article => (
                      <CompactArticleItem key={article.id} article={article} onClick={() => onSelectArticle(article.id)} />
                    ))}
                  </div>
                </div>
              ))}
              {uncategorized.length > 0 && (
                <div>
                  {categorized.length > 0 && (
                    <div className="flex items-center gap-1.5 px-2 mb-1">
                      <FileText className="w-3 h-3 text-muted-foreground" />
                      <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">General</span>
                      <span className="text-xs text-muted-foreground">({uncategorized.length})</span>
                    </div>
                  )}
                  <div className="space-y-1">
                    {uncategorized.map(article => (
                      <CompactArticleItem key={article.id} article={article} onClick={() => onSelectArticle(article.id)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function highlightInText(text: string, query: string) {
  if (!query || query.length < 2) return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: (string | { highlighted: string })[] = [];
  let lastEnd = 0;
  let idx = lowerText.indexOf(lowerQuery, lastEnd);
  while (idx !== -1) {
    if (idx > lastEnd) parts.push(text.substring(lastEnd, idx));
    parts.push({ highlighted: text.substring(idx, idx + query.length) });
    lastEnd = idx + query.length;
    idx = lowerText.indexOf(lowerQuery, lastEnd);
  }
  if (lastEnd < text.length) parts.push(text.substring(lastEnd));
  if (parts.length === 0) return text;
  return parts;
}

function RenderLine({ line, searchFilter }: { line: string; searchFilter?: string }) {
  const renderText = (text: string) => {
    if (!searchFilter || searchFilter.length < 2) return <>{text}</>;
    const parts = highlightInText(text, searchFilter);
    if (typeof parts === "string") return <>{parts}</>;
    return (
      <>
        {parts.map((part, j) =>
          typeof part === "string" ? (
            <span key={j}>{part}</span>
          ) : (
            <mark key={j} className="bg-yellow-300/50 dark:bg-yellow-500/30 text-foreground rounded-sm px-0.5">{part.highlighted}</mark>
          )
        )}
      </>
    );
  };

  if (line.startsWith("### ")) return <h3 className="text-sm font-semibold mt-3 mb-1">{renderText(line.replace("### ", ""))}</h3>;
  if (line.startsWith("## ")) return <h2 className="text-base font-semibold mt-4 mb-2">{renderText(line.replace("## ", ""))}</h2>;
  if (line.startsWith("# ")) return <h1 className="text-lg font-bold mt-4 mb-2">{renderText(line.replace("# ", ""))}</h1>;
  if (line.startsWith("- ")) return <li className="ml-3 text-sm">{renderText(line.replace("- ", ""))}</li>;
  if (line.startsWith("**") && line.endsWith("**")) return <p className="font-bold text-sm mt-1">{renderText(line.replace(/\*\*/g, ""))}</p>;
  if (line.trim() === "") return <br />;
  return <p className="text-sm mb-1.5">{renderText(line)}</p>;
}

function ArticleView({
  articleDetail, loading, onBack, onFeedback, feedbackPending,
}: {
  articleDetail: ArticleDetail | undefined;
  loading: boolean;
  onBack: () => void;
  onFeedback: (isHelpful: boolean) => void;
  feedbackPending: boolean;
}) {
  const [articleSearch, setArticleSearch] = useState("");

  if (loading || !articleDetail) {
    return (
      <div className="flex justify-center p-8">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  const matchCount = articleSearch.length >= 2
    ? (articleDetail.content.toLowerCase().match(new RegExp(articleSearch.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g")) ?? []).length
    : 0;

  return (
    <div className="flex flex-col" style={{ maxHeight: "470px" }}>
      <div className="flex items-center gap-2 px-3 py-2 border-b">
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onBack} data-testid="button-floating-article-back">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <p className="text-sm font-semibold truncate flex-1" data-testid="text-floating-article-title">{articleDetail.title}</p>
      </div>
      <div className="px-3 pt-2 pb-1">
        <div className="relative">
          <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Find in article..."
            value={articleSearch}
            onChange={e => setArticleSearch(e.target.value)}
            className="pl-7 h-7 text-xs"
            data-testid="input-article-find"
          />
          {articleSearch.length >= 2 && (
            <span className="absolute right-2 top-1.5 text-[10px] text-muted-foreground">
              {matchCount} {matchCount === 1 ? "match" : "matches"}
            </span>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto kb-scrollbar" style={{ maxHeight: "340px" }}>
        <div className="p-3 pr-4">
          <div className="prose dark:prose-invert prose-sm max-w-none" data-testid="text-floating-article-content">
            {articleDetail.content.split("\n").map((line, i) => (
              <RenderLine key={i} line={line} searchFilter={articleSearch.length >= 2 ? articleSearch : undefined} />
            ))}
          </div>
        </div>
      </div>
      <div className="border-t px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">Helpful?</span>
        <div className="flex items-center gap-1.5">
          <Button
            variant={articleDetail.userFeedback?.isHelpful === true ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => onFeedback(true)}
            disabled={feedbackPending}
            data-testid="button-floating-helpful"
          >
            <ThumbsUp className="w-3 h-3 mr-1" /> {articleDetail.feedback.helpful}
          </Button>
          <Button
            variant={articleDetail.userFeedback?.isHelpful === false ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => onFeedback(false)}
            disabled={feedbackPending}
            data-testid="button-floating-not-helpful"
          >
            <ThumbsDown className="w-3 h-3 mr-1" /> {articleDetail.feedback.notHelpful}
          </Button>
        </div>
      </div>
    </div>
  );
}

function getSnippet(content: string, query?: string, maxLen = 120): { text: string; matchStart: number; matchEnd: number } {
  const cleaned = content
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*/g, "")
    .replace(/^[-•]\s+/gm, "")
    .replace(/\n+/g, " ")
    .trim();

  if (!query || query.length < 2) {
    return { text: cleaned.substring(0, maxLen) + (cleaned.length > maxLen ? "..." : ""), matchStart: -1, matchEnd: -1 };
  }

  const lowerContent = cleaned.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerContent.indexOf(lowerQuery);

  if (idx === -1) {
    return { text: cleaned.substring(0, maxLen) + (cleaned.length > maxLen ? "..." : ""), matchStart: -1, matchEnd: -1 };
  }

  const contextBefore = 40;
  const start = Math.max(0, idx - contextBefore);
  const end = Math.min(cleaned.length, start + maxLen);
  const prefix = start > 0 ? "..." : "";
  const suffix = end < cleaned.length ? "..." : "";
  const snippet = cleaned.substring(start, end);

  const matchStart = prefix.length + (idx - start);
  const matchEnd = matchStart + query.length;

  return { text: prefix + snippet + suffix, matchStart, matchEnd };
}

function HighlightedText({ text, matchStart, matchEnd }: { text: string; matchStart: number; matchEnd: number }) {
  if (matchStart < 0 || matchEnd <= matchStart) {
    return <span>{text}</span>;
  }
  return (
    <span>
      {text.substring(0, matchStart)}
      <mark className="bg-yellow-300/40 dark:bg-yellow-500/30 text-foreground rounded-sm px-0.5">{text.substring(matchStart, matchEnd)}</mark>
      {text.substring(matchEnd)}
    </span>
  );
}

function CompactArticleItem({ article, onClick, kbName, searchQuery }: { article: KbArticle; onClick: () => void; kbName?: string; searchQuery?: string }) {
  const snippet = getSnippet(article.content, searchQuery);

  return (
    <div
      className="flex items-center gap-2.5 p-2 rounded-md cursor-pointer hover:bg-accent/50 transition-colors group"
      onClick={onClick}
      data-testid={`floating-article-${article.id}`}
    >
      <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{article.title}</p>
        <p className="text-xs text-muted-foreground line-clamp-2">
          <HighlightedText text={snippet.text} matchStart={snippet.matchStart} matchEnd={snippet.matchEnd} />
        </p>
        {kbName && (
          <div className="flex items-center gap-1 mt-0.5">
            <Library className="w-2.5 h-2.5 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground">{kbName}</span>
          </div>
        )}
      </div>
      <ChevronRight className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
    </div>
  );
}
